import { forwardRef } from 'react';
import { Button as MuiButton, CircularProgress } from '@mui/material';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';

const { semantic } = colorTokens;

const VARIANT_STYLES = {
  Primary: {
    bgcolor: semantic.action.primary.bg,
    color: semantic.action.primary.text,
    border: `1px solid ${semantic.action.primary.border}`,
    '&:hover': {
      bgcolor: semantic.text.secondary,
      borderColor: semantic.text.secondary,
      color: semantic.text.inverse,
    },
    '&:active': {
      bgcolor: semantic.text.muted,
      borderColor: semantic.text.muted,
    },
  },
  Secondary: {
    bgcolor: semantic.action.secondary.bg,
    color: semantic.action.secondary.text,
    border: `1px solid ${semantic.action.secondary.border}`,
    '&:hover': {
      bgcolor: semantic.bg.surface,
      borderColor: semantic.border.default,
    },
    '&:active': {
      bgcolor: semantic.bg.input,
    },
  },
  Accent: {
    bgcolor: colorTokens.brand.primary,
    color: semantic.text.primary,
    border: `1px solid ${colorTokens.brand.primary}`,
    '&:hover': {
      bgcolor: colorTokens.brand.primaryLight,
      borderColor: colorTokens.brand.primaryLight,
    },
    '&:active': {
      bgcolor: colorTokens.brand.primaryDark,
      borderColor: colorTokens.brand.primaryDark,
    },
  },
  Danger: {
    bgcolor: semantic.error,
    color: semantic.text.primary,
    border: `1px solid ${semantic.error}`,
    '&:hover': {
      filter: 'brightness(1.1)',
    },
    '&:active': {
      filter: 'brightness(0.9)',
    },
  },
  Ghost: {
    bgcolor: 'transparent',
    color: semantic.text.secondary,
    border: '1px solid transparent',
    '&:hover': {
      bgcolor: semantic.action.secondary.bg,
      borderColor: semantic.border.subtle,
      color: semantic.text.primary,
    },
    '&:active': {
      bgcolor: semantic.bg.surface,
    },
  },
};

const DISABLED_STYLE = {
  bgcolor: semantic.action.secondary.bg,
  color: semantic.text.disabled,
  border: `1px solid ${semantic.border.subtle}`,
  opacity: 1,
};

/**
 * Shared Action / Button (Figma Base).
 * @param {'Primary'|'Secondary'|'Accent'|'Danger'|'Ghost'} [variant]
 * @param {boolean} [loading]
 * @param {boolean} [fullWidth]
 */
export const Button = forwardRef(function Button(
  {
    variant = 'Primary',
    loading = false,
    disabled = false,
    fullWidth = false,
    children,
    sx,
    ...rest
  },
  ref,
) {
  const resolvedVariant = VARIANT_STYLES[variant] ? variant : 'Primary';
  const isDisabled = disabled || loading;
  const variantSx = VARIANT_STYLES[resolvedVariant];

  return (
    <MuiButton
      ref={ref}
      disableElevation
      disabled={isDisabled}
      fullWidth={fullWidth}
      sx={{
        minHeight: 40,
        px: 2,
        borderRadius: `${radiusTokens.lg - 2}px`,
        textTransform: 'none',
        fontFamily: typographyTokens.fontFamily,
        fontWeight: typographyTokens.fontWeightBold,
        fontSize: 13,
        letterSpacing: '0.26px',
        lineHeight: '18px',
        boxShadow: 'none',
        ...variantSx,
        '&.Mui-disabled': {
          ...DISABLED_STYLE,
        },
        ...sx,
      }}
      {...rest}
    >
      {loading ? (
        <CircularProgress
          size={18}
          thickness={5}
          sx={{ color: 'inherit', mr: children ? 1 : 0 }}
        />
      ) : null}
      {children}
    </MuiButton>
  );
});

export default Button;
